import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Button, Input, Tabs } from 'antd';
import { GrayOfflineReportViewMapIcon, LockIcon, SearchIcon } from 'components/assets/Icons/Icons';
import classNames from 'classnames';
import styles from './order-management.module.scss';
import './tabs-antd-overrides.css';
import { Conversation } from 'features/offline-reports/order-management/components/conversation/conversation';
import { PlacerLink } from 'ui-components/placer-link/placer-link';
import { useOfflineReportsActions } from 'store/offline-reports/use-offline-reports-actions';
import { useSelector } from 'react-redux';
import queryString from 'query-string';
import {
    selectOfflineReportsAccountRequests,
    selectOfflineReportsClosedRequests,
    selectOfflineReportsOpenRequests,
    selectOfflineReportsRequestsLoading,
    selectOfflineReportsSharedWithMeRequests,
} from 'store/offline-reports/selectors/offline-reports-selectors-factory';
import { Entity, OrderManagementRequest, TableActiveTab } from 'core/entities';

import RequestsTable, {
    RecordType,
} from 'features/offline-reports/order-management/components/requests-table/requests-table';
import { SharedWithMeRequestTable } from 'features/offline-reports/order-management/components/request-table-sharing-tab/request-table-sharing-tab';
import { Loading } from 'ui-components/loading/loading-component';
import { Tooltip } from 'ui-components/tooltip/tooltip';
import { useExtraReportsPermissions } from 'features/offline-reports/xtra-iframe/hooks/use-offline-report-permissions';
import { useHistory } from 'react-router-dom';
import { useRelativeUrl } from 'hooks/use-relative-url';
import { getFeatureFlags } from 'core/flow-control';
import { AccountRequestTable } from 'features/offline-reports/order-management/components/request-table-account-tab/request-table-account-tab';
import { useIsFreemiumUser } from 'hooks/use-freemium-user/use-freemium-user';
import { useAppUiActions } from 'store/app-ui/use-app-ui-actions';

const { TabPane } = Tabs;

const formatEntity = (entity: Entity) => {
    if (!entity.name && !entity.address) {
        return '';
    }
    return `${entity.name ?? ''}${entity.name && entity.address ? ', ' : ' '}${
        entity.address ?? ''
    }`;
};

export const formatPoiData = (poiDataList: string) => {
    const entitiesData = poiDataList.split('|');

    return {
        numOfEntities: entitiesData.length,
        ui: (
            <div className={styles.entitiesTooltipDataWrapper}>
                <div className={styles.entitiesTooltipHeaderWrapper}>
                    <h4>
                        <b> Selected POIs </b>
                    </h4>
                </div>
                <div className={styles.entitiesTooltipPoiWrapper}>
                    {entitiesData.map((key) => {
                        const entityData = key.split('/');
                        const entityName = entityData[0];
                        let entityAddress = entityData[1];

                        if (entityData.length > 2) {
                            entityAddress = 'N/A';
                        }

                        return (
                            <p>
                                <b>{entityName}</b> / {entityAddress}
                            </p>
                        );
                    })}
                </div>
            </div>
        ),
    };
};

export const ReportTypeComponent: FC<{
    isConversation?: boolean;
    record: RecordType | OrderManagementRequest;
}> = ({ isConversation, record }) => {
    const { reportName, entities, readReceipt, poiDataList } = record;
    const entityCount = entities.filter((entity) => entity.name !== null).length;
    const firstEntity = entities[0];
    let poiDataComponent: { numOfEntities: number; ui: ReactNode } = {
        numOfEntities: 0,
        ui: <></>,
    };

    if (poiDataList && poiDataList?.trim().length > 0) {
        poiDataComponent = formatPoiData(poiDataList);
    }

    const { numOfEntities, ui } = poiDataComponent;

    return (
        <div
            className={classNames(styles.reportTypeComponent, {
                [styles.withReadDot]: !isConversation && !readReceipt,
                [styles.fromConversation]: isConversation,
            })}
        >
            <div>
                <Tooltip placement="topLeft" title={reportName}>
                    <div className={styles.reportTypeName}>
                        {reportName}
                        {!isConversation && !readReceipt && <span className={styles.readDot} />}
                    </div>
                </Tooltip>

                {!isConversation && (
                    <p className={styles.entityDetails}>
                        <span className={styles.firstEntity}>{formatEntity(firstEntity)}</span>
                        {!numOfEntities
                            ? entityCount > 1 && (
                                  <div className={styles.entitiesTooltipWrapper}>
                                      <Tooltip
                                          title={
                                              <div className={styles.entitiesTooltipDataWrapper}>
                                                  <div
                                                      className={
                                                          styles.entitiesTooltipHeaderWrapper
                                                      }
                                                  >
                                                      <h4>
                                                          <b> Selected POIs </b>
                                                      </h4>
                                                  </div>
                                                  <div className={styles.entitiesTooltipPoiWrapper}>
                                                      <p>
                                                          <b>{entities[1].name}</b> /{' '}
                                                          {entities[1].address}
                                                      </p>
                                                  </div>
                                              </div>
                                          }
                                          className={styles.entitiesTooltip}
                                      >
                                          <span className={styles.multipleEntitiesCounter}>
                                              +{entityCount - 1} more
                                          </span>
                                      </Tooltip>
                                  </div>
                              )
                            : numOfEntities > 1 && (
                                  <div className={styles.entitiesTooltipWrapper}>
                                      <Tooltip title={ui} className={styles.entitiesTooltip}>
                                          <span className={styles.multipleEntitiesCounter}>
                                              +{numOfEntities - 1} more
                                          </span>
                                      </Tooltip>
                                  </div>
                              )}
                    </p>
                )}
            </div>
        </div>
    );
};

export const OrderManagement = () => {
    const [requestId, setRequestId] = useState<string | null>(null);
    const [searchValue, setSearchValue] = useState('');
    const [isCustomLoading, setIsCustomLoading] = useState(true);
    const urlPath = useRelativeUrl();
    const getPathName = () => {
        const sessionStoragePathItem = sessionStorage.getItem('sourceFeatureName');

        if (sessionStoragePathItem && sessionStoragePathItem.includes('advanced-reports')) {
            return '/advanced-reports/placer-xtra/';
        }

        return '/insights/placer-xtra/';
    };

    const { tab } = queryString.parse(urlPath.split('?')[1]) as {
        tab: TableActiveTab;
    };

    const DEFAULT_TAB = 'completed';

    const [activeTab, setActiveTab] = useState<TableActiveTab>(tab || DEFAULT_TAB);

    const { getOfflineReportsRequests, getOfflineReportsUserAccountInfo } =
        useOfflineReportsActions();

    const { enable_xtra_requests_admin_view_ff } = getFeatureFlags();

    useEffect(() => {
        getOfflineReportsRequests();
        getOfflineReportsUserAccountInfo();
    }, [getOfflineReportsRequests, getOfflineReportsUserAccountInfo]);

    const isLoading = useSelector(selectOfflineReportsRequestsLoading);
    const openRequests = useSelector(selectOfflineReportsOpenRequests);
    const closedRequests = useSelector(selectOfflineReportsClosedRequests);
    const accountRequests = useSelector(selectOfflineReportsAccountRequests);
    const sharedWithMeRequests = useSelector(selectOfflineReportsSharedWithMeRequests);
    const { hasReportSharingPermission, hasXtraRequestsTablePermission } =
        useExtraReportsPermissions();
    const sectionContainerClasses = classNames('tabs-antd-overrides', styles.sectionContainer);

    const unreadClosedTickets = closedRequests.filter((request) => !request.readReceipt).length;
    const unreadOpenTickets = openRequests.filter((request) => !request.readReceipt).length;
    const pathName = getPathName();
    const history = useHistory();
    const isFreemiumUser = useIsFreemiumUser();
    const { openUpgradePlanPopup } = useAppUiActions();

    const orderManagementWrapperStyles = classNames(styles.orderManagement, {
        [styles.sharedLoadingWrapper]: isCustomLoading,
    });

    useEffect(() => {
        setActiveTab(tab ?? DEFAULT_TAB);
    }, [tab]);

    const onTabChange = (key: string) => {
        history.push(`?tab=${key}`);
        setActiveTab(key as TableActiveTab);
    };

    const tabTitle = hasXtraRequestsTablePermission ? (
        <>
            My Reports
            {(unreadClosedTickets > 0 || unreadOpenTickets > 0) && (
                <div className={styles.unreadCounter}>
                    {unreadClosedTickets + unreadOpenTickets}
                </div>
            )}
        </>
    ) : (
        <>
            Completed
            {unreadClosedTickets > 0 && (
                <div className={styles.unreadCounter}>{unreadClosedTickets}</div>
            )}
        </>
    );

    const XtraLinkButton = (
        <PlacerLink
            className={styles.catalogButton}
            to={isFreemiumUser ? {} : { pathname: pathName }}
        >
            <Button
                className={classNames(styles.button, { [styles.locked]: isFreemiumUser })}
                onClick={() => {
                    isFreemiumUser && openUpgradePlanPopup();
                }}
            >
                <span className={styles.iconWrap}>
                    <span className={styles.icon}>
                        {isFreemiumUser ? <LockIcon /> : <GrayOfflineReportViewMapIcon />}
                    </span>
                </span>
                <span className={styles.text}>Placer XTRA Report Catalog</span>
            </Button>
        </PlacerLink>
    );
    return (
        <div className={orderManagementWrapperStyles}>
            <Loading show={isLoading}>
                <div>
                    {requestId && (
                        <Conversation
                            reportObject={
                                hasXtraRequestsTablePermission
                                    ? openRequests.find(
                                          (req) => Number(req.id) === Number(requestId),
                                      ) ??
                                      closedRequests.find(
                                          (req) => Number(req.id) === Number(requestId),
                                      ) ??
                                      accountRequests.find(
                                          (req) => Number(req.id) === Number(requestId),
                                      ) ??
                                      null
                                    : (activeTab === 'progress'
                                          ? openRequests.find(
                                                (req) => Number(req.id) === Number(requestId),
                                            )
                                          : activeTab === 'adminView'
                                          ? accountRequests.find(
                                                (req) => Number(req.id) === Number(requestId),
                                            )
                                          : activeTab === 'shared'
                                          ? sharedWithMeRequests.find(
                                                (req) => Number(req.id) === Number(requestId),
                                            )
                                          : closedRequests.find(
                                                (req) => Number(req.id) === Number(requestId),
                                            )) ?? null
                            }
                            requestId={requestId}
                            setRequestId={setRequestId}
                            setIsConversationOpen={() => setRequestId(null)}
                            notSendRead={activeTab === 'adminView'}
                        />
                    )}
                    <div className={styles.orderManagementHeader}>
                        <h1 className={styles.title}>Placer XTRA</h1>
                        {XtraLinkButton}
                    </div>

                    <div className={sectionContainerClasses}>
                        <Tabs
                            className={styles.tabs}
                            activeKey={activeTab}
                            onChange={onTabChange}
                            tabBarExtraContent={
                                <Input
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    className={styles.searchField}
                                    placeholder="Search Report"
                                    suffix={<SearchIcon style={{ width: 16 }} />}
                                />
                            }
                        >
                            <TabPane
                                {...{
                                    key: 'completed',
                                    tab: (
                                        <div
                                            className={classNames(styles.tabName, {
                                                [styles.activeTab]: activeTab === 'completed',
                                            })}
                                        >
                                            {tabTitle}
                                        </div>
                                    ),
                                }}
                            >
                                <RequestsTable
                                    searchValue={searchValue}
                                    setRequestId={setRequestId}
                                    requests={
                                        hasXtraRequestsTablePermission
                                            ? closedRequests.concat(openRequests)
                                            : closedRequests
                                    }
                                    activeTab={activeTab}
                                    showMaterials
                                    showShare
                                />
                            </TabPane>
                            {hasReportSharingPermission && (
                                <TabPane
                                    {...{
                                        key: 'shared',
                                        tab: (
                                            <div
                                                className={classNames(styles.tabName, {
                                                    [styles.activeTab]: activeTab === 'shared',
                                                })}
                                            >
                                                Shared With Me
                                            </div>
                                        ),
                                    }}
                                >
                                    <SharedWithMeRequestTable
                                        setIsCustomLoading={setIsCustomLoading}
                                        setRequestId={setRequestId}
                                        searchValue={searchValue}
                                        activeTab={activeTab}
                                    />
                                </TabPane>
                            )}
                            {!hasXtraRequestsTablePermission && (
                                <TabPane
                                    {...{
                                        key: 'progress',

                                        tab: (
                                            <div
                                                className={classNames(styles.tabName, {
                                                    [styles.activeTab]: activeTab === 'progress',
                                                })}
                                            >
                                                In Progress
                                                {unreadOpenTickets > 0 && (
                                                    <div className={styles.unreadCounter}>
                                                        {unreadOpenTickets}
                                                    </div>
                                                )}
                                            </div>
                                        ),
                                    }}
                                >
                                    <RequestsTable
                                        searchValue={searchValue}
                                        setRequestId={setRequestId}
                                        requests={openRequests}
                                        activeTab={activeTab}
                                    />
                                </TabPane>
                            )}
                            {enable_xtra_requests_admin_view_ff && (
                                <TabPane
                                    {...{
                                        key: 'adminView',
                                        tab: (
                                            <div
                                                className={classNames(styles.tabName, {
                                                    [styles.activeTab]: activeTab === 'adminView',
                                                })}
                                            >
                                                Admin View
                                            </div>
                                        ),
                                    }}
                                >
                                    <AccountRequestTable
                                        setIsCustomLoading={setIsCustomLoading}
                                        setRequestId={setRequestId}
                                        searchValue={searchValue}
                                        activeTab={activeTab}
                                    />
                                </TabPane>
                            )}
                        </Tabs>
                    </div>
                </div>
            </Loading>
        </div>
    );
};
